import type { ProfileCircleColor, SupportedLanguage, SupportedLocale } from 'zaptic-types'
import { getMimeTypeFromExtension } from '../../lib/mimeTypes.no-deps'
import type { DateString, Identifiable, Nominal, Nullable, UUID } from '../../lib/typeHelpers.no-deps'
import type { FileSelectExtension } from '../files/fileExtensions.no-deps'
import type { FileId } from '../files/types.no-deps'
import type { TPoints } from '../gamification/types.no-deps'
import type * as Teams from '../teams/types.no-deps'
import type { UserPermissions } from './permissions'

export const profileCircleColors: ProfileCircleColor[] = ['purple', 'blue', 'green', 'pink', 'orange', 'red'] as const

export type AccountProfile = {
    id: string
    name: string
    profileImageUrl: Nullable<string>
    profileCircleColor: ProfileCircleColor
}

export type AccountSyncProfileInput = { customer: number; lastSyncTime?: DateString | null }
export type AccountSyncProfileOutput = { profiles: AccountProfile[]; syncTime: DateString }

export interface CustomLink {
    label: string
    value: string
}

export interface ProfileInfo {
    contacts?: {
        emails?: string[]
        links?: CustomLink[]
        msTeams?: string[]
        phoneNumbers?: string[]
        whatsApp?: string[]
    }
    image?: FileId | null
    /* biographical information (eg self-introduction)  */
    bio?: string | null
}

// Types specific to user profile contact details
export const AllContactTypes = ['phone', 'email', 'whatsApp', 'msTeams', 'url'] as const
export type ContactItemType = (typeof AllContactTypes)[number]

interface InvalidEmailFound {
    type: 'InvalidEmailFound'
    email: string
}
interface InvalidMsTeamsFound {
    type: 'InvalidMsTeamsFound'
    msTeams: string
}
interface InvalidPhoneNumberFound {
    type: 'InvalidPhoneNumberFound'
    phoneNumber: string
}
interface InvalidWhatsAppFound {
    type: 'InvalidWhatsAppFound'
    whatsApp: string
}

export type ContactInfoValidationError =
    | InvalidEmailFound
    | InvalidMsTeamsFound
    | InvalidPhoneNumberFound
    | InvalidWhatsAppFound

export const MaxImageHeight = 768
export const MaxImageWidth = 768

export const ProfileImageConstraints = {
    maxImageHeight: MaxImageHeight,
    maxImageWidth: MaxImageWidth,
    maxImageSizeInBytes: 5 * 1024 * 1024,
    maxImageSizeHumanFriendly: '5MB',
    allowedFileExtensions: ['.png', '.jpg', '.jpeg', '.webp'] as const satisfies FileSelectExtension[],
} as const

const profileImageAllowedMimetypes = ProfileImageConstraints.allowedFileExtensions.map(getMimeTypeFromExtension)

export function isFileTypeValidForProfileImage(type: string) {
    return type && profileImageAllowedMimetypes.includes(type)
}

export type UserProfile = {
    userId: number
    name: string
    jobTitle: string
    profileImageUrl: Nullable<string>
    profileCircleColor: ProfileCircleColor
    profileInfo: ProfileInfo
    // this is probably going to be just a reflection on what privs the user has. But, we need to be
    // careful not to leak security information. So, a small abstraction is needed here. Also, we could have several
    // access flags using the same priv for it to make more sense in the app code.
    access?: {
        confidentialReports: boolean
    }
    // These optional fields have been added for the sync user profiles endpoint
    // They can be changed to mandatory if needed
    email?: string
    officeName?: string
    teams?: UserTeam[]
}

export type UserTeam = { id: number; name?: string; isMember?: boolean; isAdmin?: boolean; isTrainingAdmin?: boolean }

export type UsersSyncProfileInput = { lastSyncTime?: DateString | null }
export type UsersSyncProfileOutput = { profiles: UserProfile[]; syncTime: DateString; deletedProfiles: number[] }

export interface EmailPreferences {
    reportStatusChange: boolean
    addedAsCollaborator: boolean
    commentOnOwnedPost: boolean
    commentOnCommentedPost: boolean
    addedToTeam: boolean
    addedToSchedule: boolean
    reminders: boolean
    reportEdited: boolean
}

export interface NotificationUser {
    email: string
    id: number
    locale: SupportedLanguage
    emailPreferences: EmailPreferences
}

export interface AppUser {
    id: number
    isDeleted: boolean
    ssoId: UUID
    email: string
    language: SupportedLanguage
    locale: SupportedLocale
    zapticStaff: boolean
    lastSync: string
    name: string
    nickname: string
    jobTitle: string
    office: number
    officeName: string
    town: string
    createdAt: string
    activatedAt: string
    blockedAt: string
    lastModified: string
    isLegacy: boolean
    isThirdPartyAccount: boolean
    privs: UserPermissions
    points: { [name: string]: number }
    totalPoints: number
    teams: RecursiveTeams[]
    administratedTeams: RecursiveTeams[]
    administratedTrainingsOfTeams: RecursiveTeams[]
    country: string
    prefs?: {
        reportMode?: string
        castedVotes?: {
            [key: number]: number
        }
        audio_theme?: string
        vibrate?: string
    }
    profileImageUrl: string | null
    profileCircleColor: ProfileCircleColor
    profileInfo: ProfileInfo
    expertise: number[]
}

export interface AppUserProfile extends UserProfile {
    isDeleted: boolean
}

export interface RecursiveTeams {
    id: number
    name: string
    children: RecursiveTeams[]
    isMember?: boolean
    isAdmin?: boolean
    isTrainingAdmin?: boolean
}

export interface ISimpleUser {
    forename: string
    surname: string
}

export function isDeleted(user: AppUserProfile) {
    return user.isDeleted
}

export type UserId = Nominal<number, 'UserId'>
export const assertUserId = (userId: number) => userId as UserId

// This is really NewUser but we want to export NewUser as an already-filled in Team type
interface BaseUser {
    // Password is optional in all cases apart from new user
    password?: string
    forename: string
    surname: string
    email: string
    privs: UserPermissions
    office: number
    language: SupportedLanguage
    prefs: any // TODO type the user prefs
    jobTitle: string
    managedExternally?: boolean
    profileInfo: ProfileInfo

    /** If the user has uploaded a profile image (i.e. {@linkcode ProfileInfo.image}), then a link to that.
     *
     * Note that this is separate from the {@linkcode AccountProfile.profileImageUrl} property (stored in the auth database).
     *
     * Note: ideally this would not be nullable (optional should be sufficient), but due to the complexity of our type hierarchy
     * (ie `UserAccount extends GettableUserWithoutTeams extends ExtendedUser extends Identifiable<BaseUser>`) this property
     * gets merged with the `AccountProfile.profileImageUrl` property, which *is* nullable.
     */
    profileImageUrl?: string | null
}

// Additional properties that are present in nearly all cases of the user
export interface ExtendedUser extends Identifiable<BaseUser> {
    points: TPoints
    totalPoints: number
    zapticStaff: boolean
    expertise: number[]
    /** The `activatedAt` value which has been synced from the `account` table in the auth database
     * to the `users` table.  It's prefixed 'synced' here to distinguish it from the `Account.activatedAt`
     * property (which is a string, and so can't be merged with this property)
     */
    syncedActivatedAt: Nullable<Date>

    // These should one day go away
    country: string
    officeName: string
    town: string
    foreignIds: ForeignIds
}

export interface TableUser {
    id: number
    ssoId: UUID
    name: Nullable<string>
    forename: string
    surname: string
    email: string
    password: string
    privs: UserPermissions
    language: SupportedLanguage
    prefs: any
    jobTitle: string
    zapticStaff: boolean
    points: TPoints
    totalPoints: number
    lastSync: Date
    activatedAt: Date
    lastMod: Date
    isDeleted: boolean
    foreignIds: { [key: string]: string }
    emailPreferences: EmailPreferences
    office: number
    managedExternally: boolean
    profileInfo: ProfileInfo
    nickname?: string | null
    locale?: string | null
    preferences?: unknown | null
}

export type ForeignIds = { [key: string]: string }
export type WithTeams<U, T> = U & {
    teams: T[]
    administratedTeams: T[]
    administratedTrainingsOfTeams?: T[]
}

export interface AddableUser extends WithTeams<BaseUser, number> {
    password: string
    ssoId: string
    foreignIds?: ForeignIds
    isDeleted?: boolean
    emailPreferences?: EmailPreferences
    syncedActivatedAt?: Nullable<Date>
}

export type UpsertUser = Partial<AddableUser> & Pick<AddableUser, 'email'>

// If we have teams we have to have both team arrays on
export interface EditableUserWithoutTeams extends Partial<ExtendedUser & { isDeleted: boolean }> {}

export interface EditableUserWithTeams extends WithTeams<EditableUserWithoutTeams, number> {}

export type EditableUser = EditableUserWithTeams | EditableUserWithoutTeams
export type UpdatableUser = Pick<
    EditableUserWithoutTeams,
    | 'id'
    | 'email'
    | 'forename'
    | 'surname'
    | 'jobTitle'
    | 'language'
    | 'points'
    | 'totalPoints'
    | 'prefs'
    | 'profileInfo'
> & { name?: string; nickname?: string }
export type GettableUser = WithTeams<GettableUserWithoutTeams, Teams.NestedTeam>

export interface GettableUserWithoutTeams extends ExtendedUser {
    ssoId?: string
    isDeleted?: boolean
    password?: string
    lastSync: Nullable<Date>
}

type IdIdentification = { id: number; email?: BaseUser['email'] }
type SSOIdentification = { ssoId: string; id?: number; email?: BaseUser['email'] }
export type UserIdentification = IdIdentification | { email: BaseUser['email'] }
export type UserIdentificationWithSSO = SSOIdentification | UserIdentification
export type GetOptions = {
    withPassword?: boolean
    withSSOId?: boolean
    getDeleted?: boolean
}
export type TrainingHistory = {
    procedure: string
    workflow: string
    date: Date
    reportId: number
    authorId: number
    scheduleInstanceId?: Nullable<UUID>
}
